// passkey-vault.ts — Web-only WebAuthn / passkey wrap of the E2E master key.
//
// Stores the master key as `{credentialId, prfSalt, iv, wrappedKey}` in a
// dedicated IndexedDB so the raw bytes never sit in storage. The wrapping
// key is derived by the PRF (pseudo-random function) extension on a
// platform passkey: the same prfSalt + same passkey always produces the
// same 32-byte secret, but the secret never leaves the authenticator —
// the browser hands it to script only after a user-verification ceremony.
//
// Runtime model:
//   • enroll(id, rawKey)  — interactive create() flow. Caller must invoke
//                           from a user gesture. Wraps the key, persists
//                           the record, caches the PRF in memory.
//   • unlock(id)          — interactive get() flow. Caller must invoke
//                           from a user gesture. Returns the unwrapped
//                           key bytes or null on cancel / unsupported PRF.
//   • disable(id)         — wipes the wrapped record + clears the cache.
//   • isEnrolled(id)      — IDB lookup; safe to call without a gesture.
//
// Recovery: not provided — losing the passkey loses the wrapped key.
// Callers should keep the user's exported key backup (DevicePairing.exportKeyBackup)
// or re-pair from another device. A future Argon2id-wrapped recovery
// passphrase would slot in alongside the wrapped record without changes
// to the call sites of this module.

import { createSingleStore } from '../utils/idb-helpers.js';
import { CryptoEngine } from './crypto-engine.js';

/** Bytes we allocate ourselves, pinned to `<ArrayBuffer>` (see crypto-engine). */
type Bytes = Uint8Array<ArrayBuffer>;

// Thrown by unlock() when the WebAuthn ceremony succeeded but the PRF
// output failed to unwrap the stored master key. This is qualitatively
// different from user-cancel (which still returns null): the user did
// satisfy the verification prompt, but the resulting PRF bytes don't
// match the wrap. Callers can surface a distinct "key cannot be
// recovered with this passkey" path — typically a prompt to re-pair
// from another device or restore from backup — instead of treating it
// as a transient lock.
export class PasskeyPrfMismatchError extends Error {
  constructor(message = 'Passkey PRF output did not unlock the stored key.') {
    super(message);
    this.name = 'PasskeyPrfMismatchError';
  }
}

const _DB_NAME = 'NotesAppPasskeyDB';
const _DB_VERSION = 1;
const _STORE = 'wrapped_keys';

// In-memory PRF cache. Keyed by id (mk_<userId>); cleared on tab close
// by garbage collection. Used by future re-wrap calls in the same
// session so saving doesn't re-prompt for verification.
//
// The cached bytes ARE the master-unlock material — leaving them in
// the heap indefinitely turns the laptop's RAM into a passive
// exfiltration target whenever the user steps away. Drop them once the
// tab has been hidden for PRF_IDLE_MS, and again on bfcache 'pagehide'.
// The next read will re-derive via a passkey ceremony, which is a small
// re-auth cost in exchange for a strict idle-zero-out invariant.
const _prfCache = new Map<string, Bytes>();
const _PRF_IDLE_MS = 5 * 60 * 1000;
let _prfIdleTimer: any = null;

function _clearPrfCache(reason?: string): void {
  if (_prfCache.size === 0) return;
  for (const bytes of _prfCache.values()) {
    if (bytes?.fill) {
      try {
        bytes.fill(0);
      } catch {
        /* best-effort zeroing */
      }
    }
  }
  _prfCache.clear();
  if (_prfIdleTimer) {
    clearTimeout(_prfIdleTimer);
    _prfIdleTimer = null;
  }
  if (reason) console.log('[passkey-vault] PRF cache cleared:', reason);
}

function _armPrfIdleTimer(): void {
  if (typeof document === 'undefined') return;
  if (_prfIdleTimer) clearTimeout(_prfIdleTimer);
  if (document.hidden) {
    _prfIdleTimer = setTimeout(() => _clearPrfCache('tab idle'), _PRF_IDLE_MS);
  }
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', _armPrfIdleTimer);
}
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  // bfcache restoration is essentially a fresh session — clear so the
  // pre-suspend bytes aren't reused under a possibly-different user.
  window.addEventListener('pagehide', () => _clearPrfCache('pagehide'));
}

const _store = createSingleStore({
  dbName: _DB_NAME,
  version: _DB_VERSION,
  store: _STORE,
  keyPath: 'id',
});
const _idbGet = (id: string) => _store.get(id);
const _idbPut = (record: any) => _store.put(record);
const _idbDelete = (id: string) => _store.delete(id);

function _randomBytes(n: number): Bytes {
  return crypto.getRandomValues(new Uint8Array(n));
}
// Convert a Uint8Array into a fresh ArrayBuffer for WebAuthn fields
// (PublicKeyCredential members are spec'd as BufferSource but some
// browsers historically required ArrayBuffer specifically).
function _toAB(u8: Bytes): ArrayBuffer {
  return u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength);
}
function _u8(ab: ArrayBuffer): Bytes {
  return new Uint8Array(ab);
}

// SubtleCrypto helpers: pass Uint8Arrays directly. `subtle.importKey`,
// `encrypt`, and `decrypt` all accept any BufferSource, and a typed
// array is identified via ArrayBuffer.isView (realm-agnostic) — wrapping
// through `.buffer.slice()` would yield a jsdom-realm ArrayBuffer that
// Node's webcrypto rejects with "is not instance of ArrayBuffer, ..."
// when the test runner uses Node webcrypto over a jsdom-realm typed
// array.
async function _wrap(
  prfBytes: BufferSource,
  rawKey: BufferSource
): Promise<{ iv: Uint8Array; ct: Uint8Array }> {
  const wk = await CryptoEngine.importKeyForWrap(prfBytes, ['encrypt']);
  const { iv, ciphertext } = await CryptoEngine.encryptAesGcm(rawKey, wk);
  return { iv, ct: ciphertext };
}
async function _unwrap(
  prfBytes: BufferSource,
  iv: BufferSource,
  ct: BufferSource
): Promise<Uint8Array> {
  const wk = await CryptoEngine.importKeyForWrap(prfBytes, ['decrypt']);
  return CryptoEngine.decryptAesGcm(iv, ct, wk);
}

function _isAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    !!(
      window.navigator?.credentials &&
      typeof window.navigator.credentials.create === 'function' &&
      typeof window.navigator.credentials.get === 'function'
    )
  );
}

export const PasskeyVault = {
  isAvailable: _isAvailable,

  async isEnrolled(id: string): Promise<boolean> {
    try {
      const rec = await _idbGet(id);
      return !!rec?.wrappedKey;
    } catch {
      return false;
    }
  },

  /**
   * Wrap rawKey under a freshly-created passkey for `id`. Must be called
   * from a user gesture (button click). On success the in-memory PRF
   * cache is populated for the rest of the session.
   */
  async enroll(id: string, rawKey: BufferSource): Promise<boolean> {
    if (!_isAvailable()) throw new Error('Passkeys are not supported in this browser.');

    const prfSalt = _randomBytes(32);
    const userIdBytes = new TextEncoder().encode(id);
    const rpId = window.location?.hostname || 'localhost';

    const cred = await window.navigator.credentials.create({
      publicKey: {
        rp: { id: rpId, name: 'Ariadne Notes' },
        user: {
          id: _toAB(userIdBytes),
          name: id,
          displayName: `Ariadne Notes (${id})`,
        },
        challenge: _toAB(_randomBytes(32)),
        // ES256 + RS256 covers every platform authenticator.
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },
          { type: 'public-key', alg: -257 },
        ],
        authenticatorSelection: {
          residentKey: 'preferred',
          userVerification: 'required',
        },
        // The PRF extension isn't in the lib's WebAuthn input type yet.
        extensions: { prf: { eval: { first: _toAB(prfSalt) } } } as any,
      },
    });
    if (!cred) throw new Error('Passkey creation cancelled.');
    // `create()` is typed `Credential | null`; the PRF/rawId members live on
    // PublicKeyCredential and the PRF extension output isn't in the lib type.
    const pkCred = cred as any;

    const ext =
      typeof pkCred.getClientExtensionResults === 'function'
        ? pkCred.getClientExtensionResults()
        : null;
    const prfFirst = ext?.prf?.results?.first;
    if (!prfFirst) {
      throw new Error(
        'This authenticator does not support the WebAuthn PRF extension. Try a different device or browser.'
      );
    }
    const prfBytes = _u8(prfFirst);
    _prfCache.set(id, prfBytes);

    const { iv, ct } = await _wrap(prfBytes, rawKey);
    await _idbPut({
      id,
      credentialId: _u8(pkCred.rawId),
      prfSalt,
      iv,
      wrappedKey: ct,
      createdAt: Date.now(),
    });
    return true;
  },

  /**
   * Re-derive the PRF for `id` and unwrap the master key. Must be
   * called from a user gesture (browsers won't surface the WebAuthn UI
   * otherwise). Returns null on user-cancel / abort, when no record is
   * enrolled, or when the authenticator doesn't return PRF bytes.
   * Throws {@link PasskeyPrfMismatchError} when the WebAuthn ceremony
   * succeeded but the PRF output failed to decrypt the wrapped key —
   * the stored record cannot be recovered with this passkey.
   */
  async unlock(id: string): Promise<Uint8Array | null> {
    const rec = await _idbGet(id);
    if (!rec?.wrappedKey) return null;

    let prfBytes = _prfCache.get(id);
    if (!prfBytes) {
      let assertion: any;
      try {
        assertion = await window.navigator.credentials.get({
          publicKey: {
            challenge: _toAB(_randomBytes(32)),
            allowCredentials: [{ type: 'public-key', id: _toAB(rec.credentialId) }],
            userVerification: 'required',
            extensions: { prf: { eval: { first: _toAB(rec.prfSalt) } } } as any,
          },
        });
      } catch (e: any) {
        // NotAllowedError covers cancel + timeout. Other errors bubble
        // up to the caller's try/catch so they can surface a status.
        if (e && (e.name === 'NotAllowedError' || e.name === 'AbortError')) return null;
        throw e;
      }
      if (!assertion) return null;
      // `get()` is typed `Credential | null`; PRF members live on
      // PublicKeyCredential and the PRF output isn't in the lib type.
      const pkAssertion = assertion as any;
      const ext =
        typeof pkAssertion.getClientExtensionResults === 'function'
          ? pkAssertion.getClientExtensionResults()
          : null;
      const prfFirst = ext?.prf?.results?.first;
      if (!prfFirst) return null;
      prfBytes = _u8(prfFirst);
      _prfCache.set(id, prfBytes);
    }

    try {
      return await _unwrap(prfBytes, rec.iv, rec.wrappedKey);
    } catch {
      // PRF mismatch — drop the cached value so the next unlock re-prompts,
      // and surface a typed error so callers can distinguish this from a
      // user-cancel (which returns null above).
      _prfCache.delete(id);
      throw new PasskeyPrfMismatchError();
    }
  },

  async disable(id: string): Promise<void> {
    _prfCache.delete(id);
    await _idbDelete(id);
  },
};
